import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import productData from "../Datas/productData";
import { formatPrice, getProductPrice } from "../Utils/product";
import ProductImage from "./ProductImage";
import "../css/shop.scss";

const PAGE_SIZE = 12;

const categoryLabels = {
  "cooking-ingredients": "Cooking Ingredients",
  "baking-ingredients": "Baking Ingredients",
  "spice-kits": "Spice Kits",
  "salt-pepper": "Salts + Peppercorns",
  tea: "Loose Leaf Teas",
  kitchenware: "Kitchenware",
  "gift-cards": "Gift Cards",
  sale: "Sale",
};

const categoryAliases = {
  "cooking-ingredients": [
    "spices",
    "herbs",
    "seasonings",
    "dried-chiles",
    "mushrooms",
    "cooking-ingredients",
  ],
  "baking-ingredients": [
    "baking-ingredients",
    "baking-spices",
    "vanilla",
    "cacao",
    "sweeteners",
  ],
  "salt-pepper": [
    "salt-pepper",
    "salts-peppercorns",
    "gourmet-sea-salts",
    "peppercorns",
  ],
  tea: ["tea", "teas", "loose-leaf-teas"],
};

const slugify = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const asArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];

const productCategorySlug = (product) =>
  slugify(product.categorySlug || product.category);

const productTokens = (product) =>
  [
    product.categorySlug,
    product.category,
    ...asArray(product.collections),
    ...asArray(product.tags),
  ].map(slugify);

const isSaleProduct = (product) =>
  Number(product.compareAtPrice) > getProductPrice(product) ||
  productTokens(product).includes("sale");

const matchesQueryCategory = (product, queryCategory) => {
  if (!queryCategory) return true;
  if (queryCategory === "sale") return isSaleProduct(product);

  const tokens = productTokens(product);
  const aliases = categoryAliases[queryCategory] || [queryCategory];
  return aliases.some((alias) => tokens.includes(alias));
};

const getSearchText = (product) =>
  [
    product.name,
    product.brand,
    product.sku,
    product.category,
    product.origin,
    product.taste,
    product.ingredients,
    ...asArray(product.collections),
    ...asArray(product.tags),
    ...asArray(product.dietary),
  ]
    .flat()
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const getProductHref = (product) => `/shopDetails/${product.id}`;

function Shop() {
  const [searchParams] = useSearchParams();
  const searchQuery = (searchParams.get("search") || "").trim();
  const queryCategory = slugify(searchParams.get("category") || "");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedDietary, setSelectedDietary] = useState([]);
  const [selectedHeat, setSelectedHeat] = useState([]);
  const [sortBy, setSortBy] = useState("featured");
  const [viewMode, setViewMode] = useState("grid");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setSelectedCategories([]);
    setSelectedDietary([]);
    setSelectedHeat([]);
    setPage(1);
  }, [queryCategory, searchQuery]);

  const baseProducts = useMemo(() => {
    const normalizedSearch = searchQuery.toLowerCase();

    return productData.filter(
      (product) =>
        matchesQueryCategory(product, queryCategory) &&
        (!normalizedSearch || getSearchText(product).includes(normalizedSearch))
    );
  }, [queryCategory, searchQuery]);

  const categoryOptions = useMemo(() => {
    const categories = new Map();
    baseProducts.forEach((product) => {
      const value = productCategorySlug(product);
      if (value && !categories.has(value)) {
        categories.set(value, product.category || value);
      }
    });

    return [...categories.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [baseProducts]);

  const dietaryOptions = useMemo(
    () =>
      [...new Set(baseProducts.flatMap((product) => asArray(product.dietary)))]
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b))),
    [baseProducts]
  );

  const heatOptions = useMemo(
    () =>
      [...new Set(baseProducts.map((product) => product.heat))]
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b))),
    [baseProducts]
  );

  const filteredProducts = useMemo(() => {
    const products = baseProducts.filter((product) => {
      const categoryMatches =
        selectedCategories.length === 0 ||
        selectedCategories.includes(productCategorySlug(product));
      const dietary = asArray(product.dietary);
      const dietaryMatches =
        selectedDietary.length === 0 ||
        selectedDietary.every((item) => dietary.includes(item));
      const heatMatches =
        selectedHeat.length === 0 || selectedHeat.includes(product.heat);

      return categoryMatches && dietaryMatches && heatMatches;
    });

    return [...products].sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "price-low") {
        return getProductPrice(a) - getProductPrice(b);
      }
      if (sortBy === "price-high") {
        return getProductPrice(b) - getProductPrice(a);
      }
      if (sortBy === "rating") {
        return Number(b.rating || 0) - Number(a.rating || 0);
      }
      return Number(b.featured) - Number(a.featured) || a.id - b.id;
    });
  }, [baseProducts, selectedCategories, selectedDietary, selectedHeat, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setPage(1);
  }, [selectedCategories, selectedDietary, selectedHeat, sortBy]);

  const visibleProducts = filteredProducts.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );
  const firstResult = filteredProducts.length ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastResult = Math.min(page * PAGE_SIZE, filteredProducts.length);
  const activeFilterCount =
    selectedCategories.length + selectedDietary.length + selectedHeat.length;

  const pageTitle = searchQuery
    ? `Search results for “${searchQuery}”`
    : categoryLabels[queryCategory] ||
      (queryCategory
        ? queryCategory.replace(/-/g, " ")
        : "Shop All Ingredients");

  const toggleFilter = (setter, value) => {
    setter((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    );
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedDietary([]);
    setSelectedHeat([]);
  };

  const goToPage = (nextPage) => {
    setPage(nextPage);
    document.getElementById("shop-results")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const renderFilterGroup = (title, options, selected, setter, getValue) => {
    if (!options.length) return null;

    return (
      <fieldset className="shop-filter__group">
        <legend>{title}</legend>
        <div className="shop-filter__options">
          {options.map((option) => {
            const value = getValue ? getValue(option) : option;
            const label = typeof option === "object" ? option.label : option;
            return (
              <label className="shop-filter__option" key={value}>
                <input
                  type="checkbox"
                  checked={selected.includes(value)}
                  onChange={() => toggleFilter(setter, value)}
                />
                <span>{label}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  };

  return (
    <main className="shop-page main" id="main-content">
      <div className="shop-shell">
        <nav className="shop-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">{pageTitle}</span>
        </nav>

        <header className="shop-heading">
          <p className="shop-heading__eyebrow">Explore sickzuno</p>
          <h1>{pageTitle}</h1>
          <p>
            Discover fragrant spices, herbs and hard-to-find ingredients for
            everyday cooking, baking and bold culinary experiments.
          </p>
        </header>

        <div className="shop-layout" id="shop-results">
          <aside className="shop-filter" aria-label="Filter products">
            <div className="shop-filter__heading">
              <h2>Shop By</h2>
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearFilters}>
                  Clear all ({activeFilterCount})
                </button>
              )}
            </div>

            {renderFilterGroup(
              "Category",
              categoryOptions,
              selectedCategories,
              setSelectedCategories,
              (option) => option.value
            )}
            {renderFilterGroup(
              "Dietary Preferences",
              dietaryOptions,
              selectedDietary,
              setSelectedDietary
            )}
            {renderFilterGroup(
              "Heat Level",
              heatOptions,
              selectedHeat,
              setSelectedHeat
            )}
          </aside>

          <section className="shop-results" aria-labelledby="products-heading">
            <h2 className="shop-visually-hidden" id="products-heading">
              Products
            </h2>

            <div className="shop-toolbar">
              <p className="shop-toolbar__count" aria-live="polite">
                Items {firstResult}–{lastResult} of {filteredProducts.length}
              </p>

              <div className="shop-toolbar__controls">
                <div className="shop-view-toggle" aria-label="Product view">
                  <button
                    type="button"
                    className={viewMode === "grid" ? "is-active" : ""}
                    aria-pressed={viewMode === "grid"}
                    onClick={() => setViewMode("grid")}
                  >
                    <span className="shop-view-toggle__grid" aria-hidden="true" />
                    Grid
                  </button>
                  <button
                    type="button"
                    className={viewMode === "list" ? "is-active" : ""}
                    aria-pressed={viewMode === "list"}
                    onClick={() => setViewMode("list")}
                  >
                    <span className="shop-view-toggle__list" aria-hidden="true" />
                    List
                  </button>
                </div>

                <label className="shop-sort">
                  <span>Sort By</span>
                  <select
                    value={sortBy}
                    onChange={(event) => setSortBy(event.target.value)}
                  >
                    <option value="featured">Featured</option>
                    <option value="name">Product Name</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                    <option value="rating">Customer Rating</option>
                  </select>
                </label>
              </div>
            </div>

            {visibleProducts.length > 0 ? (
              <div
                className={`shop-product-list shop-product-list--${viewMode}`}
              >
                {visibleProducts.map((product) => {
                  const productHref = getProductHref(product);
                  const price = getProductPrice(product);
                  const rating = Math.max(
                    0,
                    Math.min(5, Math.round(Number(product.rating) || 0))
                  );

                  return (
                    <article className="catalog-card" key={product.id}>
                      <Link
                        className="catalog-card__image-link"
                        to={productHref}
                        aria-label={`View ${product.name}`}
                      >
                        {product.label && (
                          <span className="catalog-card__badge">
                            {product.label}
                          </span>
                        )}
                        <ProductImage
                          product={product}
                          className="catalog-card__image"
                          ariaLabel={product.name}
                        />
                      </Link>

                      <div className="catalog-card__body">
                        <p className="catalog-card__category">
                          {product.category}
                        </p>
                        <h3>
                          <Link to={productHref}>{product.name}</Link>
                        </h3>
                        <div className="catalog-card__rating">
                          <span
                            className="catalog-card__stars"
                            aria-label={`${product.rating || 0} out of 5 stars`}
                          >
                            <span aria-hidden="true">
                              {"★".repeat(rating)}
                              {"☆".repeat(5 - rating)}
                            </span>
                          </span>
                          <span>{Number(product.reviews) || 0} Reviews</span>
                        </div>
                        <div className="catalog-card__price">
                          {product.variants?.length > 1 && <small>As low as</small>}
                          <strong>{formatPrice(price)}</strong>
                          {isSaleProduct(product) && (
                            <del>{formatPrice(product.compareAtPrice)}</del>
                          )}
                        </div>
                        <p className="catalog-card__description">
                          {asArray(product.description)[0] ||
                            `Explore the flavor and aroma of ${product.name}.`}
                        </p>
                        <Link className="catalog-card__action" to={productHref}>
                          View Product
                        </Link>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="shop-empty" role="status">
                <p className="shop-heading__eyebrow">No products found</p>
                <h2>Try a broader flavor search</h2>
                <p>
                  Clear the current filters or browse all ingredients to keep
                  exploring.
                </p>
                {activeFilterCount > 0 ? (
                  <button type="button" onClick={clearFilters}>
                    Clear filters
                  </button>
                ) : (
                  <Link to="/shop">Browse all products</Link>
                )}
              </div>
            )}

            {totalPages > 1 && (
              <nav className="shop-pagination" aria-label="Product pages">
                <button
                  type="button"
                  disabled={page === 1}
                  onClick={() => goToPage(page - 1)}
                >
                  Previous
                </button>
                <div className="shop-pagination__pages">
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                    (pageNumber) => (
                      <button
                        type="button"
                        className={pageNumber === page ? "is-active" : ""}
                        aria-current={pageNumber === page ? "page" : undefined}
                        aria-label={`Page ${pageNumber}`}
                        onClick={() => goToPage(pageNumber)}
                        key={pageNumber}
                      >
                        {pageNumber}
                      </button>
                    )
                  )}
                </div>
                <button
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => goToPage(page + 1)}
                >
                  Next
                </button>
              </nav>
            )}
          </section>
        </div>
      </div>
    </main>
  );
}

export default Shop;
